import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileUpload } from "@/components/ui/file-upload";
import { Button } from "@/components/ui/button";
import { FilePdf, ImageSquare, Download, Trash, Paperclip } from "@phosphor-icons/react";

/**
 * Multi-file attachments list for any entity (session, cert, item, etc).
 *
 * Uses the existing GET /api/files?entity_type=…&entity_id=… endpoint to
 * enumerate previously-uploaded files, POST /api/files/upload to add new ones,
 * and DELETE /api/files/{id} for soft-delete.
 *
 * Downloads use a same-origin URL — the browser attaches the httpOnly
 * access_token cookie automatically, so a plain <a> tag can trigger the
 * download without any JS-readable secret in the URL.
 */
export function FileAttachments({ entityType, entityId, readOnly = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!entityType || !entityId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/files`, {
        params: { entity_type: entityType, entity_id: entityId },
      });
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [entityType, entityId]); // eslint-disable-line

  const onUploaded = () => load();

  const remove = async (id) => {
    if (!window.confirm("Remove this attachment?")) return;
    try {
      await api.delete(`/files/${id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadUrl = (id) => {
    // Same-origin `<a href>` — the browser auto-attaches the httpOnly
    // access_token cookie. No JS-readable secret exposed in the URL.
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    return `${base}/api/files/${id}`;
  };

  const fmtBytes = (n) => {
    if (!n) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };

  if (!entityType || !entityId) return null;
  return (
    <div className="space-y-3">
      {!readOnly && (
        <FileUpload
          entityType={entityType}
          entityId={entityId}
          variant="attachment"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          onChange={onUploaded}
        />
      )}
      {loading && <div className="text-xs text-muted-foreground">Loading attachments…</div>}
      {!loading && items.length === 0 && (
        <div className="text-xs text-muted-foreground italic flex items-center gap-1.5">
          <Paperclip size={12} /> No attachments yet.
        </div>
      )}
      {items.length > 0 && (
        <div className="divide-y divide-border border border-border rounded-md" data-testid="file-attachments-list">
          {items.map((f) => {
            const isPdf = (f.content_type || "").includes("pdf");
            return (
              <div key={f.id} className="px-3 py-2 flex items-center gap-3" data-testid={`file-row-${f.id}`}>
                <div className="h-8 w-8 rounded bg-muted flex items-center justify-center shrink-0 text-muted-foreground">
                  {isPdf ? <FilePdf size={16} weight="duotone" /> : <ImageSquare size={16} weight="duotone" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{f.original_filename || f.id}</div>
                  <div className="text-[11px] text-muted-foreground">{fmtBytes(f.size)} · {(f.created_at || "").slice(0, 10)}</div>
                </div>
                <a
                  href={downloadUrl(f.id)}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  data-testid={`file-download-${f.id}`}
                >
                  <Download size={14} /> Open
                </a>
                {!readOnly && (
                  <Button
                    size="icon" variant="ghost"
                    onClick={() => remove(f.id)}
                    className="h-8 w-8 text-red-600 hover:text-red-700"
                    data-testid={`file-delete-${f.id}`}
                  >
                    <Trash size={14} />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
